// Serviços
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { patch } from "@/services/api";
import { useLoader } from "@/context/GeneralContext";
import { toast } from "@/hooks/use-toast";
import useVerify from "@/hooks/use-verify";
import { formatCPF } from "@/utils/cpf-mask";
// Template Page
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/general-components/ConfirmDialog";
import Icon from "@/components/general-components/Icon";
import HeaderRow from "@/components/general-components/HeaderRow";
import PermissionsForm from "./UserFormPermissions";
// Interfaces
import { IEntity } from "../-interfaces/entity.interface";
import { ApiError } from "@/general-interfaces/api.interface";
import ResponsiveBadge from "@/components/general-components/ResponsiveBadge";

interface UserItemProps {
  user: IEntity;
  index: number;
  setFormData: (data: IEntity) => void;
  setFormType: (type: string) => void;
  setOpenForm: (open: boolean) => void;
}

const UserItem = ({ user, index, setFormData, setFormType, setOpenForm }: UserItemProps) => {
  const { can, is } = useVerify();
  const queryClient = useQueryClient();
  const { showLoader, hideLoader } = useLoader();

   // Mutation para inativar o usuário
  const { mutate: deactivateUser } = useMutation({
    mutationFn: (userId: number) => {
      showLoader(`Inativando usuário ${user.name}...`);
      return patch<IEntity>('user', `inactive/${userId}`);
    },
    onSuccess: () => {
      hideLoader();
      toast({
        title: `Usuário ${user.name} inativado!`,
        description: "Usuário inativado com sucesso",
        variant: "success",
        duration: 5000
      })
      queryClient.invalidateQueries({ queryKey: ['listCompanyUsers'] });
    },
    onError: (error: unknown) => {
      hideLoader();
      toast({
        title: "Erro ao inativar o usuário!",
        description: error instanceof Error 
          ? error.message 
          : typeof error === 'object' && error !== null && 'response' in error 
            ? ((error as ApiError).response?.data?.message || "Erro ao inativar o usuário!")
            : "Erro ao inativar o usuário!",
        variant: "destructive",
        duration: 5000
      })
    }
  });

  // Mutation para ativar o usuário
  const { mutate: activateUser } = useMutation({
    mutationFn: (userId: number) => {
      showLoader(`Ativando usuário ${user.name}...`);
      return patch<IEntity>('user', `active/${userId}`);
    },
    onSuccess: () => {
      hideLoader();
      toast({
        title: `Usuário ${user.name} reativado!`,
        description: "Usuário foi reativado com sucesso",
        variant: "success",
        duration: 5000
      })
      queryClient.invalidateQueries({ queryKey: ['listCompanyUsers'] });
    },
    onError: (error: unknown) => {
      hideLoader();
      toast({
        title: "Erro ao reativar o usuário!",
        description: error instanceof Error 
          ? error.message 
          : typeof error === 'object' && error !== null && 'response' in error 
            ? ((error as ApiError).response?.data?.message || "Erro ao reativar o usuário!")
            : "Erro ao reativar o usuário!",
        variant: "destructive",
        duration: 5000
      })
    }
  });

  const handleConfirmAction = (actionType: "activate" | "deactivate") => {
    if (!user.id) return;

    if (actionType === "activate") {
      activateUser(user.id);
    } else {
      deactivateUser(user.id);
    }
  };

  // validar se é super e se o usuário é o super
  const canEdit = user.profile?.name !== 'super' ? true : is('super');

  return (
    <>
      {/* Renderiza o Header apenas no primeiro item */}
      <HeaderRow show={index === 0}>
        <div className="w-3/12">Usuário</div>
        <div className="w-2/12">Perfil</div>
        <div className="w-3/12">Contatos</div>
        <div className="w-2/12">Criado em</div>
        <div className="w-1/12">Status</div>
        <div className="w-1/12">Ações</div>
      </HeaderRow>

      {/* Conteúdo do item */}
      <div className={`${index % 2 === 0 ? "bg-background" : "bg-background/50"} shadow-sm rounded relative gap-2 lg:gap-0 flex flex-col lg:flex-row lg:items-center justify-between p-4 w-full border-b`}>
        {/* Badges com ScrollArea */}
        <div className="absolute -top-1 left-4 right-14 lg:right-auto lg:left-4 flex gap-1">
          { user.isSeller && (
              <ResponsiveBadge
                icon="user-round-check"
                text="Vendedor"
                colorClass="text-inverse-foreground bg-primary"
              />
            )
          }

          { user.isSeller && user.sellerStatus && (
              <ResponsiveBadge
                icon={user.sellerStatus === 'PENDING' ? 'clock' : user.sellerStatus === 'ACTIVE' ? 'check-circle' : user.sellerStatus === 'SUSPENDED' ? 'ban' : user.sellerStatus === 'BLOCKED' ? 'x-circle' : 'x-circle'}
                text={user.sellerStatus === 'PENDING' ? 'Vendedor Pendente' : user.sellerStatus === 'ACTIVE' ? 'Vendedor Integrado' : user.sellerStatus === 'SUSPENDED' ? 'Vendedor Suspenso' : 'Vendedor Bloqueado'}
                colorClass={user.sellerStatus === 'PENDING' ? 'text-yellow-900 bg-yellow-200' : user.sellerStatus === 'ACTIVE' ? 'text-green-900 bg-green-200' : user.sellerStatus === 'SUSPENDED' ? 'text-orange-900 bg-orange-200' : 'text-red-900 bg-red-200'}
              />
            )
          }
        </div>
        
        {/* Avatar e Nome */}
        <div className="w-full lg:w-3/12 flex items-center space-x-4">
          <Avatar className="border">
            <AvatarImage src={user.imageUrl || undefined} alt={user.name} />
            <AvatarFallback>{user.name[0]}</AvatarFallback>
          </Avatar>
          <div className="break-words w-9/12 md:w-full">
            <h2 className="text-sm font-semibold">{user.name}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-100">{formatCPF(user.cpf)}</p>
          </div>
        </div>

        {/* Cargo */}
        <div className="lg:w-2/12 flex items-baseline gap-2">
          <p className="lg:hidden text-sm font-medium text-gray-800 dark:text-gray-300">Perfil: </p>
          <Badge
            variant="outline"
            className="rounded dark:border-primary"
          >
            {user.profile?.name}
          </Badge>
        </div>

        {/* Contatos */}
        <div className="lg:w-3/12 flex flex-col">
          <div className="flex gap-2">
            <p className="lg:hidden text-sm font-medium text-gray-800 dark:text-gray-300">Telefone: </p>
            <p className="text-sm text-gray-600 dark:text-gray-100">{user.phone}</p>
          </div>
          <div className="flex gap-2 break-all">
            <p className="lg:hidden text-sm font-medium text-gray-800 dark:text-gray-300">Email: </p>
            <p className="text-xs text-gray-600 dark:text-gray-100">{user.email}</p>
          </div>
        </div>

        {/* Data de Criação */}
        <div className="lg:w-2/12 flex items-baseline gap-2">
          <p className="lg:hidden text-sm font-medium text-gray-800 dark:text-gray-300">Cadastro: </p>
          <p className="text-sm text-gray-600 dark:text-gray-100">
            {new Date(user.createdAt || '2024-01-01').toLocaleDateString()}
          </p>
        </div>

        {/* Status */}
        <div className="lg:w-1/12 flex items-baseline gap-2">
          <p className="lg:hidden text-sm font-medium text-gray-800 dark:text-gray-300">Status: </p>
          <Badge
            variant="outline"
            className={`${
              user.active
              ? "bg-green-200 text-green-900 dark:bg-green-900 dark:text-green-200"
              : "bg-red-200 text-red-900 dark:bg-red-900 dark:text-red-200"
            } rounded-full px-2 py-1 text-xs`}
          >
            {user.active ? "Ativo" : "Inativo"}
          </Badge>
        </div>

        {/* Ações */}
        <div className="absolute top-2 right-2 lg:static lg:w-1/12">
        {
          canEdit && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  className="h-8 w-8 rounded-full p-0 text-gray-700"
                  variant="outline"
                  size="sm">
                    <Icon name="ellipsis-vertical" className="text-foreground dark:text-primary w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>

                { can('update_user') && (
                    <Button 
                      variant="ghost" 
                      className="flex justify-start gap-2 p-2 items-baseline w-full h-fit"
                      onClick={() => {
                        setFormData(user);
                        setFormType("");
                        setOpenForm(true);
                      }}
                    >
                      <Icon name="edit-3" className="w-3 h-3" /> 
                      <p>Editar</p>
                    </Button>
                  )
                }

                { can('update_user_password') && (
                    <Button 
                      variant="ghost" 
                      className="flex justify-start gap-2 p-2 items-baseline w-full h-fit"
                      onClick={() => {
                        setFormData(user);
                        setFormType("only");
                        setOpenForm(true);
                      }}
                    >
                      <Icon name="key-square" className="w-3 h-3" /> 
                      <p>Mudar Senha</p>
                    </Button>
                  )
                }

                { can('update_profile') && (
                    <PermissionsForm user={user} />
                  )
                }
                
                {
                  user.active ? (
                    can('inactive_user') && (
                        <DropdownMenuItem className="p-0" onSelect={(e) => e.preventDefault()}>
                          <ConfirmDialog
                            title={`Inativar o usuário ${user.name}?`}
                            description={`Ao prosseguir, o usuário ${user.name} será inativo e não poderá acessar a plataforma.`}
                            onConfirm={() => handleConfirmAction("deactivate")}
                            titleBttn="Inativar"
                            iconBttn="power-off"
                          />
                        </DropdownMenuItem>
                    )
                  ) : (
                    can('activate_user') && (
                      <DropdownMenuItem className="p-0" onSelect={(e) => e.preventDefault()}>
                        <ConfirmDialog
                          title={`Reativar o usuário ${user.name}?`}
                          description={`Ao prosseguir, o usuário ${user.name} será reativado e poderá acessar a plataforma.`}
                          onConfirm={() => handleConfirmAction("activate")}
                          titleBttn="Reativar"
                          iconBttn="power"
                        />
                      </DropdownMenuItem>
                    )
                  )
                }
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
        </div>
      </div>
    </>
  )
};

export default UserItem;
